import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgencyData {
  id: string;
  name: string;
  settings: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgencySettings() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ data: AgencyData }>({
    queryKey: ["agency-settings"],
    queryFn: () => api.get("/admin/config/settings"),
  });

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [passwordMinLength, setPasswordMinLength] = useState("12");
  const [sessionTimeout, setSessionTimeout] = useState("30");

  useEffect(() => {
    if (data?.data) {
      const s = data.data.settings;
      setName(data.data.name ?? "");
      setAddress((s.address as string) ?? "");
      setCity((s.city as string) ?? "");
      setState((s.state as string) ?? "");
      setZip((s.zip as string) ?? "");
      setPhone((s.phone as string) ?? "");
      setPasswordMinLength(String(s.passwordMinLength ?? 12));
      setSessionTimeout(String(s.sessionTimeout ?? 30));
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.put("/admin/config/settings", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-settings"] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate({
      name,
      address,
      city,
      state,
      zip,
      phone,
      settings: {
        passwordMinLength: parseInt(passwordMinLength, 10),
        sessionTimeout: parseInt(sessionTimeout, 10),
      },
    });
  }

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-lg border bg-muted" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Agency Settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure your agency's information and security policies
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Agency Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Agency Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Agency Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
              <Input
                label="State"
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
              <Input
                label="ZIP"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
            <Input
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </CardContent>
        </Card>

        {/* Security Policies */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Security Policies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Minimum Password Length"
                type="number"
                min={8}
                max={64}
                value={passwordMinLength}
                onChange={(e) => setPasswordMinLength(e.target.value)}
              />
              <Input
                label="Session Timeout (minutes)"
                type="number"
                min={5}
                max={480}
                value={sessionTimeout}
                onChange={(e) => setSessionTimeout(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {updateMutation.isError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {(updateMutation.error as Error)?.message ?? "Failed to save settings"}
          </div>
        )}

        {updateMutation.isSuccess && (
          <div className="rounded-md bg-green-50 p-3 text-sm text-green-800">
            Settings saved successfully.
          </div>
        )}

        <Button type="submit" className="gap-2" loading={updateMutation.isPending}>
          <Save className="h-4 w-4" />
          Save Settings
        </Button>
      </form>
    </div>
  );
}
